import { ChangeDetectorRef, Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LayoutService } from './service/app.layout.service';
import { AppSidebarComponent } from './app.sidebar.component';
import { MenuService } from './service/app.menu.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-menuitem]',
  template: `
    <ng-container>
      <div
        *ngIf="root && item.visible !== false"
        class="layout-menuitem-root-text"
        (click)="itemClick($event)">
        <span *ngIf="!isSlim || isMobile">{{ item.label }}</span>
        <i
          class="layout-menuitem-root-icon pi pi-fw"
          [ngClass]="collapsed ? 'pi-angle-down' : 'pi-angle-up'"
          *ngIf="item.items || (isSlim && !isMobile)"
          style="margin-left: auto;"
          [pTooltip]="item.label"
          [tooltipDisabled]="!isSlim"></i>
      </div>
      <a
        *ngIf="(!item.routerLink || item.items) && item.visible !== false && !root"
        [attr.href]="item.url"
        (click)="itemClick($event)"
        (mouseenter)="onMouseEnter()"
        [ngClass]="item.class"
        [attr.target]="item.target"
        tabindex="0"
        pRipple
        [pTooltip]="item.label"
        [tooltipDisabled]="!isSlim">
        <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
        <span class="layout-menuitem-text" *ngIf="!isSlim || isMobile">{{ item.label }}</span>
        <i
          class="pi pi-fw pi-angle-up layout-submenu-toggler"
          *ngIf="item.items"
          style="margin-left: auto;"></i>
      </a>
      <a
        *ngIf="item.routerLink && !item.items && item.visible !== false && !root"
        (click)="itemClick($event)"
        (mouseenter)="onMouseEnter()"
        [ngClass]="item.class"
        [routerLink]="item.routerLink"
        routerLinkActive="active-route"
        [routerLinkActiveOptions]="
          item.routerLinkActiveOptions || {
            paths: 'exact',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          }
        "
        [fragment]="item.fragment"
        [queryParamsHandling]="item.queryParamsHandling"
        [preserveFragment]="item.preserveFragment"
        [skipLocationChange]="item.skipLocationChange"
        [replaceUrl]="item.replaceUrl"
        [state]="item.state"
        [queryParams]="item.queryParams"
        [attr.target]="item.target"
        tabindex="0"
        pRipple
        [pTooltip]="item.label"
        [tooltipDisabled]="!isSlim">
        <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
        <span class="layout-menuitem-text" *ngIf="!isSlim || isMobile">{{ item.label }}</span>
        <p-badge
          [value]="item.badge"
          [severity]="item.badgeSeverity || 'primary'"
          [styleClass]="'layout-menuitem-badge' + (item.pulseBadgeOnDisplay ? ' badge-pulse' : '')"
          *ngIf="(!isSlim || isMobile) && item.badge && item.badge > 0"></p-badge>
        <i
          class="pi pi-fw pi-angle-up layout-submenu-toggler"
          *ngIf="item.items"
          style="margin-left: auto;"></i>
      </a>

      <ul
        *ngIf="item.items && item.visible !== false && !collapsed"
        [@children]="submenuAnimation"
        (@children.done)="onSubmenuAnimated($event)">
        <ng-template ngFor let-child let-i="index" [ngForOf]="item.items">
          <li
            app-menuitem
            [item]="child"
            [index]="i"
            [parentKey]="key"
            [class]="child.badgeClass"
            [class.active-menuitem]="child.submenu"></li>
        </ng-template>
      </ul>
    </ng-container>
  `,
  animations: [
    trigger('children', [
      state(
        'collapsed',
        style({
          height: '0',
        })
      ),
      state(
        'expanded',
        style({
          height: '*',
        })
      ),
      state(
        'hidden',
        style({
          display: 'none',
        })
      ),
      state(
        'visible',
        style({
          display: 'block',
        })
      ),
      transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
    ]),
  ],
})
export class AppMenuitemComponent implements OnInit, OnDestroy {
  @Input() item: any;
  @Input() index!: number;
  @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;
  @Input() parentKey!: string;

  active = false;
  collapsed = false; // Start with all menu items in non-collapsed state

  menuSourceSubscription: Subscription | null = null; // Initialize to null
  menuResetSubscription: Subscription | null = null; // Initialize to null

  key: string = '';
  submenu = true;

  constructor(
    public layoutService: LayoutService,
    private cd: ChangeDetectorRef,
    public router: Router,
    private menuService: MenuService,
    private appSidebar: AppSidebarComponent
  ) {
    this.menuSourceSubscription = this.menuService.menuSource$.subscribe(value => {
      Promise.resolve(null).then(() => {
        if (value.routeEvent) {
          this.active =
            value.key === this.key || value.key.startsWith(this.key + '-') ? true : false;
        } else {
          if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
            this.active = false;
          }
        }
      });
    });

    this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
      this.active = false;
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(params => {
      if (this.item.routerLink) {
        this.updateActiveStateFromRoute();
      }
    });
  }

  ngOnInit() {
    this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

    if (this.item.routerLink) {
      this.updateActiveStateFromRoute();
    }
  }

  updateActiveStateFromRoute() {
    let activeRoute = this.router.isActive(this.item.routerLink[0], {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored',
    });

    if (activeRoute) {
      this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
    }
  }

  onSubmenuAnimated(event: AnimationEvent) {
    if (
      event.toState === 'visible' &&
      this.layoutService.isDesktop() &&
      this.layoutService.isSlim()
    ) {
      const el = <HTMLUListElement>event.element;
      const container = <HTMLDivElement>this.appSidebar.menuContainer.nativeElement;

      // if (this.layoutService.isSlim()) {
      //     el.style.removeProperty('left');
      //     const scrollTop = container.scrollTop;
      //     const offsetTop = el.parentElement?.offsetTop || 0;
      //     el.style.top = (offsetTop - scrollTop) + 'px';
      // }
    }
  }

  itemClick(event: any) {
    // avoid processing disabled items
    if (this.item.disabled) {
      event.preventDefault();
      return;
    }

    // navigate with hover
    if (this.root && this.isSlim) {
      this.layoutService.state.menuHoverActive = !this.layoutService.state.menuHoverActive;
    }

    // execute command
    if (this.item.command) {
      this.item.command({ originalEvent: event, item: this.item });
    }

    // toggle active state
    if (this.item.items) {
      this.active = !this.active;
      this.submenu = !this.submenu;

      if (this.root) {
        this.collapsed = !this.collapsed; // Toggle collapse state for root items
      }

      if (this.root && this.active) {
        this.layoutService.onOverlaySubmenuOpen();
      }

      var parent = event.target.parentNode;
      if (parent.classList.contains('active-menuitem')) parent.classList.remove('active-menuitem');
      else parent.classList.add('active-menuitem');
    } else {
      if (this.layoutService.isMobile()) {
        this.layoutService.state.staticMenuMobileActive = false;
      }

      // if (this.isSlim) {
      //     this.menuService.reset();
      //     this.layoutService.state.menuHoverActive = false;
      // }
    }

    this.menuService.onMenuStateChange({ key: this.key });
  }

  onMouseEnter() {
    // activate item on hover
    if (this.root && this.isSlim && this.layoutService.isDesktop()) {
      if (this.layoutService.state.menuHoverActive) {
        this.active = true;
        this.menuService.onMenuStateChange({ key: this.key });
      }
    }
  }

  toggleCollapse() {
    this.collapsed = !this.collapsed;
  }

  get submenuAnimation() {
    if (this.layoutService.isDesktop())
      // return 'visible';
      return this.submenu ? 'visible' : 'hidden';
    else {
      return this.root ? 'expanded' : this.submenu ? 'collapsed' : 'expanded';
    }
  }

  get isSlim() {
    return this.layoutService.isSlim();
  }

  get isMobile() {
    return this.layoutService.isMobile();
  }

  @HostBinding('class.active-menuitem')
  get activeClass() {
    if (!this.item.submenu) return this.active && !this.root;
    return false;
  }

  @HostBinding('class.submenu-non-background')
  get nonBackgroundClass() {
    return this.submenu && !this.active;
  }

  ngOnDestroy() {
    if (this.menuSourceSubscription) {
      this.menuSourceSubscription.unsubscribe();
    }

    if (this.menuResetSubscription) {
      this.menuResetSubscription.unsubscribe();
    }
  }
}
